import { useEffect, type ReactNode } from 'react';
import { CheckCircle2, Circle } from 'lucide-react';
import { Button } from '@/shared/components/Button';
import { FormField } from '@/shared/components/FormField';
import { Icon } from '@/shared/components/Icon';
import { Input } from '@/shared/components/Input';
import { Modal } from '@/shared/components/Modal';
import { useForgotPasswordViewModel } from '../viewmodels/useForgotPasswordViewModel';
import styles from './ForgotPasswordModal.module.scss';

export interface ForgotPasswordModalProps {
  open: boolean;
  onClose: () => void;
  /** E-mail já digitado no login, para não pedir duas vezes. */
  defaultEmail?: string;
}

/**
 * Recuperação de senha — código por e-mail, no padrão Cognito.
 *
 * Três passos em sequência, tudo dentro do mesmo modal: pedir o código,
 * verificá-lo, definir a senha nova. Fechar e reabrir recomeça do primeiro
 * passo — manter o progresso anterior daria a impressão de que um novo código
 * foi enviado.
 */
export function ForgotPasswordModal({ open, onClose, defaultEmail = '' }: ForgotPasswordModalProps) {
  const vm = useForgotPasswordViewModel(defaultEmail);

  useEffect(() => {
    if (open) vm.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const erro = vm.error ? (
    <p className={styles.error} role="alert">
      {vm.error}
    </p>
  ) : null;

  let title = 'Redefinir senha';
  let body: ReactNode;
  let footer: ReactNode;

  if (vm.step === 'concluido') {
    title = 'Senha redefinida';
    body = (
      <div className={styles.confirmation}>
        <span className={styles.icon}>
          <Icon as={CheckCircle2} size={20} />
        </span>
        <p>Sua senha foi redefinida. Já pode entrar com a senha nova.</p>
      </div>
    );
    footer = <Button onClick={onClose}>Entendi</Button>;
  } else if (vm.step === 'senha') {
    body = (
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          vm.submitPassword();
        }}
      >
        <p className={styles.intro}>Escolha a nova senha da sua conta.</p>

        <FormField
          label="Nova senha"
          required
          error={vm.passwordForm.formState.errors.password?.message}
        >
          {(props) => (
            <Input {...props} {...vm.passwordForm.register('password')} type="password" autoFocus />
          )}
        </FormField>

        <FormField
          label="Confirmar senha"
          required
          error={vm.passwordForm.formState.errors.passwordConfirmation?.message}
        >
          {(props) => (
            <Input {...props} {...vm.passwordForm.register('passwordConfirmation')} type="password" />
          )}
        </FormField>

        {vm.requisitos.length > 0 ? (
          <ul className={styles.requisitos}>
            {vm.requisitos.map((requisito) => (
              <li key={requisito.label} className={styles.requisito} data-atendido={requisito.atendido}>
                <Icon as={requisito.atendido ? CheckCircle2 : Circle} size={16} />
                {requisito.label}
              </li>
            ))}
          </ul>
        ) : null}

        {erro}
      </form>
    );
    footer = (
      <>
        <Button variant="tertiary" onClick={onClose}>
          Cancelar
        </Button>
        <Button loading={vm.isSubmitting} onClick={vm.submitPassword}>
          Redefinir senha
        </Button>
      </>
    );
  } else if (vm.step === 'codigo') {
    body = (
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          vm.submitCode();
        }}
      >
        <p className={styles.intro}>
          Enviamos um código de 4 dígitos para <strong>{vm.email}</strong>. Ele vale por 10 minutos.
        </p>

        <FormField label="Código de verificação" error={vm.codeForm.formState.errors.code?.message}>
          {(props) => (
            <Input
              {...props}
              {...vm.codeForm.register('code')}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={4}
              placeholder="0000"
              autoFocus
            />
          )}
        </FormField>

        {erro}

        <button type="button" className={styles.trocarEmail} onClick={vm.voltarParaEmail}>
          Errei o e-mail — voltar
        </button>
      </form>
    );
    footer = (
      <>
        <Button variant="tertiary" onClick={onClose}>
          Cancelar
        </Button>
        <Button loading={vm.isSubmitting} onClick={vm.submitCode}>
          Verificar código
        </Button>
      </>
    );
  } else {
    body = (
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          vm.submitEmail();
        }}
      >
        <p className={styles.intro}>
          Informe o e-mail cadastrado. Enviaremos um código de 4 dígitos para você redefinir a senha.
        </p>

        <FormField label="E-mail" required error={vm.emailForm.formState.errors.email?.message}>
          {(props) => (
            <Input
              {...props}
              {...vm.emailForm.register('email')}
              type="email"
              autoComplete="email"
              placeholder="seu@email.com.br"
            />
          )}
        </FormField>

        {erro}
      </form>
    );
    footer = (
      <>
        <Button variant="tertiary" onClick={onClose}>
          Cancelar
        </Button>
        <Button loading={vm.isSubmitting} onClick={vm.submitEmail}>
          Enviar código
        </Button>
      </>
    );
  }

  return (
    <Modal open={open} onClose={onClose} title={title} footer={footer}>
      {body}
    </Modal>
  );
}
